import type { StartRealtimeVoiceCallArgs } from './start-realtime-voice-call';
import { createRealtimeLeaseActivityReporter } from './realtime-voice-lease-activity';
import { retryRealtimeUsageReport } from './realtime-voice-lifecycle';
import type { RealtimeVoiceFailureKind } from './realtime-voice-lifecycle';
import { buildLiveVoiceUsageReport } from '@/lib/live-voice-usage';

function object(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function createLiveVoiceEventHandlers(
  args: StartRealtimeVoiceCallArgs,
  leaseSessionId: string,
  terminate: (failure: RealtimeVoiceFailureKind) => void
) {
  let usageChain = Promise.resolve();
  let finalized = false;
  const activity = createRealtimeLeaseActivityReporter({
    heartbeat: (activityKind) =>
      args.actions.heartbeatLease({
        orgThreadId: args.orgThreadId,
        sessionId: leaseSessionId,
        activityKind,
      }),
    isCurrentCall: args.isCurrentCall,
    onRenewed: (duration) =>
      args.scheduleLeaseExpiry(duration, () => terminate('connection')),
    onError: () => console.warn('[voice-call] Live lease heartbeat failed'),
  });

  return {
    onEvent(event: Record<string, unknown>) {
      if (!args.isCurrentCall()) return;
      const usage = buildLiveVoiceUsageReport(event);
      if (usage) {
        // Serial submission preserves report order. The server deduplicates
        // response IDs and cumulative duration independently of this receiver.
        const report = usageChain
          .then(() =>
            retryRealtimeUsageReport(() =>
              args.actions.reportLiveUsage({
                ...usage,
                orgThreadId: args.orgThreadId,
                sessionId: leaseSessionId,
              })
            )
          )
          .catch(() => {
            console.warn('[voice-call] Live usage report failed', {
              eventId: usage.eventId,
            });
          })
          .finally(() =>
            args.refs.pendingUsageReportsRef.current.delete(report)
          );
        usageChain = report;
        args.refs.pendingUsageReportsRef.current.add(report);
      }
      if (
        event.type === 'session.input_transcript.delta' &&
        typeof event.delta === 'string' &&
        event.delta.length > 0
      ) {
        args.responses.hasConversationActivityRef.current = true;
        activity.report('user');
      }
      if (event.type === 'session.delegation.created') {
        args.responses.hasConversationActivityRef.current = true;
      }
      if (event.type === 'session.closed') {
        finalized = true;
        // A provider expiry or safety termination ends the call even when it
        // supplies final usage. Normal UI close already owns cleanup.
        if (!args.refs.sessionRef.current?.isClosing) terminate('connection');
      }
      if (event.type === 'error' && !args.refs.sessionRef.current?.isClosing) {
        const error = object(event.error);
        console.warn('[voice-call] Live command rejected', {
          eventId:
            typeof event.event_id === 'string' ? event.event_id : undefined,
          hasClientEventId: typeof error?.client_event_id === 'string',
          rejectedContextKind:
            args.refs.sessionRef.current?.getRejectedContextKind(event) ?? null,
        });
      }
    },
    onPlaybackChange(speaking: boolean) {
      if (!args.isCurrentCall()) return;
      args.responses.assistantSpeakingRef.current = speaking;
      args.recording.assistantPlaybackActiveRef.current = speaking;
      args.recording.assistantRecordingActiveRef.current = speaking;
      if (speaking) {
        activity.report('agent');
        args.recording.startTurn(
          'assistant',
          args.recording.remoteStreamRef.current
        );
      } else {
        void args.recording.stopTurn('assistant');
      }
      args.ui.setStatus(speaking ? 'speaking' : 'listening');
    },
    onInputActivity(active: boolean) {
      if (!args.isCurrentCall()) return;
      args.ui.setIsCapturing(active);
      if (active)
        args.recording.startTurn('user', args.recording.micStreamRef.current);
      else void args.recording.stopTurn('user');
    },
    onFailure(reason: string) {
      if (!finalized && args.isCurrentCall())
        terminate(reason === 'capacity' ? 'capacity' : 'connection');
    },
  };
}
